import React, { memo, useCallback, useMemo } from 'react';
import { TextFieldProps } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { DateTimeValidationError } from '@mui/x-date-pickers/models';
import { isValid } from 'date-fns';
import { AppPickerField } from 'components/app-picker-field';
import { getPickerValidationError } from 'components/app-picker-field/validation';
import {
  AppPickerActionBar,
  PICKER_MODAL_ACTIONS,
  PICKER_MODAL_ACTIONS_WITH_TODAY,
  PickerModalProvider,
  usePickerModal,
} from 'components/app-picker-modal';
import { useI18n } from '../../i18';

/**
 * Phase D1: @material-ui/pickers 3 (KeyboardDatePicker / KeyboardDateTimePicker)
 * -> @mui/x-date-pickers. Contract unchanged: event-shaped `onChange`,
 * `error: { message }` translated into the helper text, the time picker is
 * chosen by the format carrying hours/minutes. Phase D2 (x-date-pickers 9,
 * which dropped the <input> field DOM): the field is the app's AppPickerField,
 * so it stays a plain TextField.
 *
 * Prompt 2a-fix (D-48, D-49; the prompt scopes this AurionCR file in): v3's
 * modal model -- Mobile* pickers with a Today / Cancel / OK bar (Today where
 * `showTodayButton`, v3's default here), the selection a draft until OK
 * (`components/app-picker-modal`); the field's error follows v3's built-in
 * validation.
 */
type FieldProps = Pick<
  TextFieldProps,
  'label' | 'helperText' | 'placeholder' | 'style' | 'className' | 'fullWidth' | 'size'
>;

interface Props extends FieldProps {
  onChange?: (event: { target: { value: Date | null } }) => void;
  value?: any;
  error?: { message?: string };
  format?: string;
  showTodayButton?: boolean;
  ampm?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
}
export const DatePicker = memo<Props>(
  ({
    value,
    error,
    format = 'dd/MM/yyyy HH:mm',
    showTodayButton = true,
    ampm = false,
    onChange,
    label,
    helperText,
    placeholder,
    style,
    className,
    fullWidth = true,
    size,
    ...props
  }) => {
    const { t } = useI18n();
    const value_ = useMemo(() => {
      if (value) {
        if (typeof value === 'string' && isValid(new Date(value))) {
          return new Date(value);
        } else if (value instanceof Date) {
          return value;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }, [value]);
    const isTimeAvailable = useMemo(() => {
      return /[hHm]/g.test(format);
    }, [format]);
    const onChangeWrap = useCallback(
      (date: Date | null) => {
        onChange && onChange({ target: { value: date } });
      },
      [onChange],
    );
    const modal = usePickerModal<DateTimeValidationError>({ value: value_, onChange: onChangeWrap });
    const validationError = getPickerValidationError(value_, props);
    const consumerHelperText = error && error.message ? t(error.message) || '' : helperText;
    const shared = {
      ...props,
      ...modal.pickerProps,
      format,
      label,
      // N2b / T5 (SC-N2-04, D-entry in DECISIONS.md): the modal's Hebrew toolbar title below md,
      // as the app's own pickers take it (components/app-picker-modal); undefined at md and up
      localeText: {
        todayButtonLabel: t('today'),
        okButtonLabel: t('ok'),
        cancelButtonLabel: t('cancel'),
        ...modal.localeTextBelowMd,
      },
      slots: { field: AppPickerField, actionBar: AppPickerActionBar },
      slotProps: {
        // v3's keyboard button had no edge; x-date-pickers' edge="end" pulls the field 12px narrower
        openPickerButton: { edge: false as const },
        actionBar: {
          actions: showTodayButton ? PICKER_MODAL_ACTIONS_WITH_TODAY : PICKER_MODAL_ACTIONS,
        },
        dialog: modal.dialogSlotProps,
        textField: {
          error: Boolean(error) || Boolean(validationError),
          // v3: the consumer's helper text wins; the built-in string fills an empty one.
          helperText: consumerHelperText || validationError || consumerHelperText,
          style,
          className,
          fullWidth,
          size,
          slotProps: { htmlInput: { placeholder } },
        },
      },
    };
    return (
      <PickerModalProvider actions={modal.actions}>
        <div className="date-field-wrapper">
          {isTimeAvailable && <MobileDateTimePicker {...shared} ampm={ampm} />}
          {!isTimeAvailable && <MobileDatePicker {...shared} />}
        </div>
      </PickerModalProvider>
    );
  },
);

export default DatePicker;
